import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import {
  Box,
  Card,
  Chip,
  ListItem,
  ListItemAvatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { getNiceTimestamp } from 'src/utils/get-nice-timestamp';
import { NetworkIcon } from 'src/icons/untitled-ui/duocolor/network';
// import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate } from 'react-router-dom';
import StayCurrentPortraitIcon from '@mui/icons-material/StayCurrentPortrait';
import StayCurrentLandscapeIcon from '@mui/icons-material/StayCurrentLandscape';
import DonutChart from 'src/components/chart/DonutChart';
import { useTranslation } from 'react-i18next';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export default function ScreenDetailsDialog({ open, onClose, screen }) {
  const [mapKey, setMapKey] = React.useState(0);
  const navigate = useNavigate();

  const { t } = useTranslation();

  React.useEffect(() => {
    if (open && screen) {
      // Force map to reinitialize when dialog opens
      setMapKey((prev) => prev + 1);
    }
  }, [open, screen]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      slots={{
        transition: Transition,
      }}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge='start'
            color='inherit'
            onClick={onClose}
            aria-label='close'
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ mx: 2 }} variant='h6' component='div'>
            {screen?.deviceName}
          </Typography>
          {screen?.isOnline ? (
            screen?.isInOfflineMode ? (
              <Chip color='warning' label='OFF-LINE MODE' />
            ) : (
              <Chip color='success' label='ON-LINE' />
            )
          ) : (
            <Chip color='error' label='OFF-LINE' />
          )}
          <List>
            <ListItem>
              <ListItemText
                secondary='Pairing code'
                primary={screen?.screenCode}
              />
            </ListItem>
          </List>
        </Toolbar>
      </AppBar>
      {screen ? (
        <>
          <Box
            sx={{
              p: 3,
            }}
          >
            <List
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '20px',
                '& .MuiListItem-root': {
                  width: 'auto',
                },
              }}
            >
              <ListItem>
                <ListItemText
                  secondary='Last activity'
                  primary={
                    screen.lastActive
                      ? getNiceTimestamp(new Date(screen.last_updated), true)
                      : 'N/A'
                  }
                />
              </ListItem>
              <Divider orientation='vertical' sx={{ height: '70px' }} />
              <ListItem>
                <ListItemAvatar>
                  {screen?.device_details?.connectionType == 'WiFi' ? (
                    <NetworkIcon
                      connectionType='WiFi'
                      wifiSignal={screen?.device_details?.wifiSignal}
                      ethernetStatus={0}
                    />
                  ) : null}
                </ListItemAvatar>
                <ListItemText
                  secondary='Name of wi-fi network'
                  primary={
                    screen?.device_details?.ssid
                      ? screen?.device_details?.ssid ==
                        'Not connected to a network'
                        ? 'Unable to identify network SSID'
                        : screen?.device_details?.ssid
                      : 'N/A'
                  }
                />
              </ListItem>
              <Divider orientation='vertical' sx={{ height: '70px' }} />
              <ListItem>
                <ListItemText
                  secondary='OS'
                  primary={
                    screen?.device_details?.os_version
                      ? screen?.device_details?.os_version
                      : 'N/A'
                  }
                />
              </ListItem>
              <Divider orientation='vertical' sx={{ height: '70px' }} />
              <ListItem>
                <ListItemText secondary='MAC' primary={screen?.mac} />
              </ListItem>
              <Divider orientation='vertical' sx={{ height: '70px' }} />
              <ListItem>
                <ListItemText
                  secondary='Is rooted'
                  primary={
                    screen?.device_details?.root_level == 'true' ? 'YES' : 'NO'
                  }
                />
              </ListItem>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  p: 3,
                }}
              >
                {+screen?.device_details?.width >
                +screen?.device_details?.height ? (
                  <StayCurrentLandscapeIcon
                    sx={{ display: 'block', margin: '0 auto' }}
                  />
                ) : (
                  <StayCurrentPortraitIcon
                    sx={{ display: 'block', margin: '0 auto' }}
                  />
                )}
                {screen?.device_details?.width +
                  ' x ' +
                  screen?.device_details?.height}
              </Box>
            </List>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ height: '350px', width: '100%', p: 3 }}>
              <Typography sx={{ mb: 1 }}>
                {screen?.locationDetails?.formatted_address}
              </Typography>
              {screen?.locationDetails?.lat && screen?.locationDetails?.lng ? (
                <Box
                  sx={{
                    height: 'calc(100% - 30px)',
                    width: '100%',
                    maxWidth: '500px',
                  }}
                >
                  {/* <MapContainer
                    key={mapKey}
                    center={[
                      screen?.locationDetails?.lat,
                      screen?.locationDetails?.lng,
                    ]}
                    zoom={10}
                    scrollWheelZoom={false}
                    style={{
                      height: '100%',
                      width: '100%',
                      borderRadius: '8px',
                    }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    />
                    <Marker
                      position={[
                        screen?.locationDetails?.lat,
                        screen?.locationDetails?.lng,
                      ]}
                    >
                      <Popup>User's screen location</Popup>
                    </Marker>
                  </MapContainer> */}
                      <APIProvider
                        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                        libraries={['places', 'geometry']}
                      >
                        <Map
                          mapId={'bf51a910020fa25a'}
                          disableDefaultUI={true}
                          defaultZoom={10}
                          style={{ height: '300px' }}
                          center={{
                            lat: +screen?.locationDetails?.lat,
                            lng: +screen?.locationDetails?.lng,
                          }}
                        >
                          <Marker
                            position={{
                              lat: +screen?.locationDetails?.lat,
                              lng: +screen?.locationDetails?.lng,
                            }}
                            title='Screen location'
                          />
                        </Map>
                      </APIProvider>                  
                </Box>
              ) : (
                <Typography>No location data available</Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  p: 3,
                  minHeight: '75px',
                }}
              >
              <AccountCircleIcon />                
                <Typography>{screen?.userDetails.email}</Typography>
                <Chip color='primary' label={screen?.userDetails.role} />
                <Button
                  color={
                    screen?.userDetails.plan?.name == 'Free'
                      ? 'success'
                      : screen?.userDetails.plan?.name == 'Basic'
                      ? 'primary'
                      : screen?.userDetails.plan?.name == 'Premium'
                      ? 'secondary'
                      : 'info'
                  }
                  onClick={() =>
                    navigate(`/user-subscription/${screen?.userDetails?._id}`)
                  }
                >
                  {screen?.userDetails.plan?.name}
                </Button>
                <Typography>
                  Last Login:{' '}
                  {new Date(
                    screen?.userDetails?.lastLogin
                  ).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{
            display: "flex"
          }}>
            <Box sx={{
              maxWidth: "300px",
              p:3 
            }}>
              <DonutChart
                views={t('views.screenDetail.ram')}
                series={[
                  +screen?.ram?.total.split(' ')[0] -
                    +screen?.ram?.free.split(' ')[0],
                  +screen?.ram?.free.split(' ')[0],
                ]}
                labels={[
                  t('views.screenDetail.usage', {
                    unit: screen?.ram?.total.split(' ')[1],
                  }),
                  t('views.screenDetail.free', {
                    unit: screen?.ram?.free.split(' ')[1],
                  }),
                ]}
              />
            </Box>          
            <Box sx={{
              maxWidth: "300px",
              p:3
            }}>
                       <DonutChart
                          views={t('views.screenDetail.storage')}
                          series={[
                            +screen?.storage?.total.split(' ')[0] -
                              +screen?.storage?.free.split(' ')[0],
                            +screen?.storage?.free.split(' ')[0],
                          ]}
                          labels={[
                            t('views.screenDetail.usage', {
                              unit: screen?.storage?.total.split(' ')[1],
                            }),
                            t('views.screenDetail.free', {
                              unit: screen?.storage?.free.split(' ')[1],
                            }),
                          ]}
                        />              
            </Box>
          </Box>
        </>
      ) : null}
    </Dialog>
  );
}
